import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '../config/runtime';

// Same base-URL convention as AuthContext: relative /api in Docker and prod,
// where nginx proxies to the backend. The old hardcoded http://localhost:8000
// fallback pointed the browser at the wrong origin everywhere but bare local dev.

const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Announcements are published from the Super Admin portal and land in this
// tenant's own `active_announcements`. They are NOT dismissible — the point is
// that every user sees the message on every page, so it rides along in a
// marquee above the top bar until the super admin retracts it or the window
// (show_from / show_until) closes.
const REFRESH_MS = 5 * 60 * 1000;

const SEVERITY_RANK = { info: 0, warning: 1, critical: 2 };

const SEVERITY_STYLES = {
  critical: 'bg-red-600 text-white',
  warning: 'bg-amber-400 text-amber-950',
  info: 'bg-blue-600 text-white',
};

function severityIcon(type) {
  switch (type) {
    case 'critical':
      return <AlertCircle className="h-4 w-4 flex-shrink-0" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 flex-shrink-0" />;
    default:
      return <Info className="h-4 w-4 flex-shrink-0" />;
  }
}

export default function AnnouncementBanner() {
  // `token` here is only a login/logout signal — axios' request interceptor is
  // what actually attaches the Authorization header.
  const { token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);

  const fetchAnnouncements = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/announcements/active`);
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setAnnouncements([]);
      return undefined;
    }
    fetchAnnouncements();
    const timer = setInterval(fetchAnnouncements, REFRESH_MS);
    return () => clearInterval(timer);
  }, [token, fetchAnnouncements]);

  if (announcements.length === 0) return null;

  // One strip for all messages, coloured by the loudest one on it.
  const severity = announcements.reduce(
    (worst, a) =>
      (SEVERITY_RANK[a.type] ?? 0) > (SEVERITY_RANK[worst] ?? 0) ? a.type : worst,
    'info'
  );

  // Longer text needs a longer cycle, or it whips past unreadably.
  const totalChars = announcements.reduce(
    (n, a) => n + (a.title?.length || 0) + (a.body?.length || 0) + 6,
    0
  );
  const durationSec = Math.max(20, Math.round(totalChars / 6));

  const track = (
    <div className="flex shrink-0 items-center gap-10 pr-10" aria-hidden={false}>
      {announcements.map((a) => (
        <span key={a.id} className="flex items-center gap-2 whitespace-nowrap text-sm">
          {severityIcon(a.type)}
          <span className="font-semibold">{a.title}</span>
          {a.body && <span className="opacity-90">{a.body}</span>}
        </span>
      ))}
    </div>
  );

  return (
    <div
      className={`${SEVERITY_STYLES[severity] || SEVERITY_STYLES.info} announcement-marquee overflow-hidden py-2`}
      role="status"
      aria-live="polite"
      data-testid="announcement-banner"
    >
      <style>{`
        @keyframes announcement-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .announcement-marquee__track {
          display: flex;
          width: max-content;
          animation: announcement-scroll linear infinite;
        }
        .announcement-marquee:hover .announcement-marquee__track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .announcement-marquee__track { animation: none; }
          .announcement-marquee { overflow-x: auto; }
        }
      `}</style>
      <div
        className="announcement-marquee__track"
        style={{ animationDuration: `${durationSec}s` }}
      >
        {/* duplicated so the loop point is seamless */}
        {track}
        {track}
      </div>
    </div>
  );
}
